import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

/**
 *  Admin → Felhasználók és cégek (backend/felhasznalok.js).
 *
 *  Az ügyfélkörös modulokban (Könyvelés, Bér, Számla és Kassza, Készletkezelés, Tárgyi eszköz) a modul adminja
 *  itt vesz fel felhasználót – belépési fiókkal együtt – és oszt ki rá cégeket:
 *   - ÜGYFÉL: a cég saját embere; a kiosztott cégek nevében dolgozik (Számla és Kassza, Készletkezelés), a
 *     Könyvelést az ügyfélportálon olvassa;
 *   - MUNKATÁRS: az iroda kollégája a választott jogkörrel; ha cégeket kap, csak azokat látja.
 *  A Könyvelésből a kiosztás minden kapcsolt modulra szól; önálló modulban csak arra a modulra.
 */
@Component({
  selector: 'app-admin-felhasznalok',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './felhasznalok.component.html',
  styleUrls: ['./felhasznalok.component.scss'],
})
export class AdminFelhasznalokComponent implements OnInit {
  adat: any = null;
  hiba = '';
  fut = false;
  intezmeny = '';
  alrendszer = 'NAPLO';
  /** Az űrlap: új felhasználó (`uj`) vagy egy meglévő kiosztása (`felhasznalo` kitöltve). */
  urlap: any = null;
  cegSzuro = '';
  eredmeny: any = null;
  kereso = '';

  constructor(private http: HttpClient, private data: DataService) {}

  private ut(vege = '') { return `/api/felhasznalok/${this.alrendszer}/${this.intezmeny}${vege}`; }

  async ngOnInit() {
    //  A tárolt intézményből és alrendszerből (mint a többi admin-oldal): közvetlen URL-betöltéskor az app-keret
    //  még nem regisztrált.
    this.intezmeny = (await this.data.getFromStorage('sajatIntezmeny'))?.intezmenyId || '';
    this.alrendszer = String((await this.data.getFromStorage('currentMode')) || 'NAPLO');
    await this.betolt();
  }

  async betolt() {
    this.hiba = '';
    try { this.adat = await firstValueFrom(this.http.get(this.ut())); }
    catch (e: any) { this.adat = null; this.hiba = e?.error?.error || 'A felhasználók nem tölthetők be.'; }
  }

  get celNevek(): string { return (this.adat?.celok || []).map((c: any) => c.nev).join(', '); }
  get kapcsolt(): boolean { return (this.adat?.celok || []).length > 1; }
  get felhasznalok(): any[] {
    const k = this.kereso.trim().toLowerCase();
    return (this.adat?.felhasznalok || []).filter((f: any) => !k || `${f.nev} ${f.felhasznalo} ${f.email || ''}`.toLowerCase().includes(k));
  }
  cegNev(id: string): string { return (this.adat?.cegek || []).find((c: any) => c.id === id)?.nev || 'törölt cég'; }
  jogkorNev(alr: string, j: string | null): string {
    if (!j) return '';
    return (this.adat?.jogkorok?.[alr] || []).find((x: any) => x.jogkor === j)?.nev || j;
  }
  jogkorSzoveg(f: any): string {
    return (this.adat?.celok || []).filter((c: any) => f.jogkorok?.[c.alr]).map((c: any) => `${c.nev}: ${this.jogkorNev(c.alr, f.jogkorok[c.alr])}`).join(' · ');
  }
  cegSzoveg(f: any): string {
    if (f.fajta === 'munkatars' && !f.cegek.length) return 'minden cég';
    if (!f.cegek.length) return 'nincs cég';
    const n = f.cegek.map((id: string) => this.cegNev(id));
    return n.length > 3 ? `${n.slice(0, 3).join(', ')} és még ${n.length - 3}` : n.join(', ');
  }

  ujFelhasznalo() {
    this.eredmeny = null;
    const jogkorok: Record<string, string> = {};
    for (const c of this.adat?.celok || []) jogkorok[c.alr] = '';
    //  Kényelmi alap: a nyitó modulban az első nem-admin jogkör (pl. Könyvelő).
    const sajat = (this.adat?.jogkorok?.[this.alrendszer] || []).find((x: any) => x.jogkor !== 'admin');
    if (sajat) jogkorok[this.alrendszer] = sajat.jogkor;
    this.urlap = { uj: true, vezeteknev: '', keresztnev: '', email: '', felhasznalonev: '', fajta: 'munkatars', jogkorok, cegek: new Set<string>(), kuldes: false, jelszo: '' };
    this.cegSzuro = '';
  }
  szerkeszt(f: any) {
    this.eredmeny = null;
    const jogkorok: Record<string, string> = {};
    for (const c of this.adat?.celok || []) jogkorok[c.alr] = f.jogkorok?.[c.alr] || '';
    this.urlap = { uj: false, felhasznalo: f.felhasznalo, nev: f.nev, fajta: f.fajta, jogkorok, cegek: new Set<string>(f.cegek) };
    this.cegSzuro = '';
  }
  megse() { this.urlap = null; }

  /** A modul választható jogkörei – ha a felhasználó mostani jogköre nincs köztük, az is (hogy a mentés ne vegye el). */
  jogkorOpciok(alr: string): any[] {
    const l = this.adat?.jogkorok?.[alr] || [];
    const most = this.urlap?.jogkorok?.[alr];
    return most && !l.some((x: any) => x.jogkor === most) ? [...l, { jogkor: most, nev: most }] : l;
  }
  get cegekSzurve(): any[] {
    const k = this.cegSzuro.trim().toLowerCase();
    return (this.adat?.cegek || []).filter((c: any) => !k || `${c.nev} ${c.adoszam || ''} ${c.azonosito || ''}`.toLowerCase().includes(k));
  }
  cegValt(id: string, be: boolean) { if (be) this.urlap.cegek.add(id); else this.urlap.cegek.delete(id); }
  mindKijelol(be: boolean) { for (const c of this.cegekSzurve) this.cegValt(c.id, be); }
  get vanJogkor(): boolean { return Object.values(this.urlap?.jogkorok || {}).some((v) => !!v); }
  get mentheto(): boolean {
    if (!this.urlap || this.fut) return false;
    if (this.urlap.uj && !(this.urlap.vezeteknev.trim() || this.urlap.keresztnev.trim())) return false;
    if (this.urlap.fajta === 'munkatars' && !this.vanJogkor) return false;
    if (this.urlap.fajta === 'ugyfel' && !this.urlap.cegek.size) return false;
    return true;
  }

  async ment() {
    if (!this.mentheto) return;
    this.fut = true; this.hiba = '';
    const u = this.urlap;
    const kozos = { fajta: u.fajta, jogkorok: u.fajta === 'munkatars' ? u.jogkorok : {}, cegek: [...u.cegek] };
    try {
      if (u.uj) {
        const r: any = await firstValueFrom(this.http.post(this.ut('/uj'), {
          ...kozos, vezeteknev: u.vezeteknev, keresztnev: u.keresztnev, email: u.email, felhasznalonev: u.felhasznalonev,
          kuldes: u.kuldes, jelszo: u.jelszo || undefined,
        }));
        this.eredmeny = { ...r, nev: `${u.vezeteknev} ${u.keresztnev}`.trim() };
      } else {
        await firstValueFrom(this.http.put(this.ut(`/f/${encodeURIComponent(u.felhasznalo)}`), kozos));
        this.eredmeny = null;
      }
      this.urlap = null;
      await this.betolt();
    } catch (e: any) { this.hiba = e?.error?.error || 'A mentés nem sikerült.'; }
    finally { this.fut = false; }
  }

  async ujJelszo(f: any) {
    if (!confirm(`Új ideiglenes jelszót adsz ${f.nev} részére? A régi jelszavával utána nem tud belépni.`)) return;
    this.fut = true; this.hiba = '';
    try {
      const r: any = await firstValueFrom(this.http.post(this.ut(`/f/${encodeURIComponent(f.felhasznalo)}/fiok`), {}));
      this.eredmeny = { felhasznalo: f.felhasznalo, nev: f.nev, fiok: r, jelszoCsere: true };
    } catch (e: any) { this.hiba = e?.error?.error || 'Az új jelszó nem készült el.'; }
    finally { this.fut = false; }
  }

  async megvon(f: any) {
    if (!confirm(`${f.nev} hozzáférését megvonod (${this.celNevek})? A jogköre és a cégei törlődnek; a személy és a belépési fiókja megmarad.`)) return;
    this.fut = true; this.hiba = '';
    try { await firstValueFrom(this.http.delete(this.ut(`/f/${encodeURIComponent(f.felhasznalo)}`))); await this.betolt(); }
    catch (e: any) { this.hiba = e?.error?.error || 'A megvonás nem sikerült.'; }
    finally { this.fut = false; }
  }

  async masol(t: string) { try { await navigator.clipboard.writeText(t); } catch { /* a felhasználó kézzel másolja */ } }
}
